import { createSelector } from '@reduxjs/toolkit';

import { LOADING_STATUSES, TAGS } from '~/constants/edf';
import {
    intersectionOfIntervals,
    invertIntervals,
    removeOverlappingIntervals,
} from '~/utils/interval';
import { getFriendlyLabelForTag, getParentTag, hasParentTag } from '~/services/annotationTree';
import intervalTreeService from '~/services/intervalTree';
import uniqueCache from '~/services/uniqueCache';
import { rangeSelector } from '~/selectors/chart';
import { POSITIONS_ORDER } from '~/constants/shared/edf';

import { getPositionIndex, getObjectValuesSum } from './helpers';

// region Loading Files
const loadingFilesSelector = (state) => state.app.edf.loadingFiles;
export const loadingStatusSelector = createSelector(loadingFilesSelector, ({ status }) => status);
export const isEdfLoadedSelector = createSelector(
    loadingStatusSelector,
    (status) => status === LOADING_STATUSES.SUCCESS
);
export const loadingErrorMessageSelector = createSelector(
    loadingFilesSelector,
    ({ errorMessage }) => (errorMessage ? errorMessage.toString() : errorMessage) // in case we get an array of errors
);
// endregion

// region Headers
// TODO: does the object change? mb I don't need all of these selectors
export const headersSelector = (state) => state.app.edf.info.headers;
export const recordDurationSelector = (state) => state.app.edf.info.headers.record_duration;
export const recLengthSelector = (state) => state.app.edf.info.headers.recordingLength;
export const startDateSelector = (state) => state.app.edf.info.headers.recStartDateTimeISO;
// endregion

// region Annotations

const annotationSelector = (state) => state.app.edf.info.annotations;
export const updateAnnotationStatusSelector = createSelector(
    annotationSelector,
    ({ updateAnnotationStatus }) => updateAnnotationStatus
);
export const annotationMetaSelector = (state) => state.app.edf.info.annotations.meta;
export const isVisibleSaveButtonSelector = createSelector(
    annotationSelector,
    ({ ids }) => ids.length
);
export const sortedAnnotationsSelector = createSelector(
    (state) => state.app.edf.info.annotations.ids,
    (state) => state.app.edf.info.annotations.entities,
    (ids, entities) => ids.map((id) => entities[id])
);
export const selectAnnotationByIdSelector = (id) =>
    createSelector(
        (state) => state.app.edf.info.annotations.entities,
        (entities) => entities[id]
    );

export const getAnnotationsBetweenRangeSelector = createSelector(
    (state) => state.app.edf.info.annotations.ids,
    (state) => state.app.edf.info.annotations.entities,
    rangeSelector,
    (ids, entities, [start, end]) => {
        const treeIDs = intervalTreeService.search(start, end);
        const result = treeIDs.map((id) => entities[id]);

        return result.filter(({ duration }) => duration !== 0);
    }
);

/**
 * DEVICE RECORDING TIME  //start ------ MOTION ------ DROPOUTS ------- ARTIFACT ----- end
 * TOTAL RECORDING TIME   //start ------ MOTION ------          ------- ARTIFACT ----- end
 * MONITORING TIME        //start ------        ------          -------          ----- end
 *
 * NOTE: supine/non-supine/upright should be calculated using MONITORING TIME
 */

const getInvalidMT = createSelector(sortedAnnotationsSelector, (sortedAnnotations) => {
    return sortedAnnotations.reduce((result, { onset, tag, duration }) => {
        if (hasParentTag(tag, TAGS.SIGNAL)) {
            result.push([onset, onset + duration]);
        }

        return result;
    }, []);
});

const getInvalidTRT = createSelector(sortedAnnotationsSelector, (sortedAnnotations) => {
    return sortedAnnotations.reduce((result, { onset, tag, duration }) => {
        if (tag === TAGS.DROPOUT) {
            result.push([onset, onset + duration]);
        }

        return result;
    }, []);
});

export const getValidMTIntervals = createSelector(
    (state) => state.app.edf.info.headers.recordingLength,
    getInvalidMT,
    (length, time) => invertIntervals(removeOverlappingIntervals(time), length)
);

export const getValidTRTIntervals = createSelector(
    (state) => state.app.edf.info.headers.recordingLength,
    getInvalidTRT,
    (length, time) => invertIntervals(removeOverlappingIntervals(time), length)
);

const getPositionIntervalsMap = createSelector(
    (state) => state.app.edf.info.headers.recordingLength,
    sortedAnnotationsSelector,
    (length, annotations) => {
        const intervals = [];
        const tags = new Set();
        let lastPositionType = '';
        let lastPositionInterval = [];

        for (const { tag, onset } of annotations) {
            if (getParentTag(tag) !== TAGS.POSITION) {
                continue;
            }

            if (lastPositionInterval.length === 0) {
                lastPositionType = tag;
                lastPositionInterval.push(onset);
            } else {
                lastPositionInterval.push(onset);
                intervals.push([...lastPositionInterval, lastPositionType]);
                lastPositionType = tag;
                lastPositionInterval = [onset];
            }

            tags.add(lastPositionType);
        }

        if (intervals.length === 0) {
            intervals.push([0, length, TAGS.UNKNOWN]);
        } else {
            lastPositionInterval.push(length);
            intervals.push([...lastPositionInterval, lastPositionType]);
        }

        return {
            tags,
            intervals,
        };
    }
);
const calculateEventsAndTimes = (
    getAnnotationById,
    validMTIntervals,
    validTRTIntervals,
    positionIntervals
) => {
    const monitoringTimeByPosition = {};
    const totalRecordingTimeByPosition = {};

    positionIntervals.tags.forEach((tag) => {
        const positionValidMTIntervals = intersectionOfIntervals(
            validMTIntervals,
            positionIntervals.intervals.filter(([, , _tag]) => _tag === tag)
        );
        const positionValidTRTIntervals = intersectionOfIntervals(
            validTRTIntervals,
            positionIntervals.intervals.filter(([, , _tag]) => _tag === tag)
        );

        positionValidMTIntervals.forEach(([start, end]) => {
            monitoringTimeByPosition[tag] ||= 0;
            monitoringTimeByPosition[tag] += end - start;
        });

        positionValidTRTIntervals.forEach(([start, end]) => {
            totalRecordingTimeByPosition[tag] ||= 0;
            totalRecordingTimeByPosition[tag] += end - start;
        });
    });

    const respiratoryEventsByPosition = {};
    let desaturationEvents = 0;
    validMTIntervals.forEach(([start, end]) => {
        const ids = intervalTreeService.search(start, end);

        for (const id of ids) {
            const { tag, onset } = getAnnotationById(id);

            if (tag !== TAGS.RESPIRATORY && tag !== TAGS.DESATURATION) {
                continue;
            }

            const [, , position] =
                positionIntervals.intervals[getPositionIndex(positionIntervals.intervals, onset)];

            if (tag === TAGS.RESPIRATORY) {
                respiratoryEventsByPosition[position] ||= 0;
                respiratoryEventsByPosition[position] += 1;
            }

            if (tag === TAGS.DESATURATION) {
                desaturationEvents += 1;
            }
        }
    });

    return {
        desaturationEvents,
        respiratoryEventsByPosition,
        monitoringTimeByPosition,
        totalRecordingTimeByPosition,
    };
};
const hourInSeconds = 3600;
const applicable = (time) => time && !Number.isNaN(time) && time > 600;
export const getSleepResultsSelector = createSelector(
    (state) => state.app.edf.info.annotations.entities,
    getValidMTIntervals,
    getValidTRTIntervals,
    getPositionIntervalsMap,
    (entities, validMTIntervals, validTRTIntervals, positionIntervalsMap) => {
        const getAnnotationById = (id) => entities[id];

        const {
            desaturationEvents,
            respiratoryEventsByPosition,
            monitoringTimeByPosition,
            totalRecordingTimeByPosition,
        } = calculateEventsAndTimes(
            getAnnotationById,
            validMTIntervals,
            validTRTIntervals,
            positionIntervalsMap
        );

        const monitoringTime = getObjectValuesSum(monitoringTimeByPosition);
        const { [TAGS.SUPINE]: supineEvents = 0, [TAGS.NONSUPINE]: nonSupineEvents = 0 } =
            respiratoryEventsByPosition;
        const {
            [TAGS.SUPINE]: supineTime = 0,
            [TAGS.NONSUPINE]: nonSupineTime = 0,
            [TAGS.UPRIGHT]: uprightTime = 0,
        } = monitoringTimeByPosition;
        const respiratoryEvents = getObjectValuesSum(respiratoryEventsByPosition);

        const result = {
            respiratoryEvents,
            supineEvents,
            nonSupineEvents,
            desaturations: desaturationEvents,
            validTime: getObjectValuesSum(totalRecordingTimeByPosition),
            uprightTime: uprightTime,
            monitoringTime,
            supineTime: supineTime,
            nonSupineTime: nonSupineTime,
            AHI: applicable(monitoringTime)
                ? (respiratoryEvents * hourInSeconds) / monitoringTime
                : 0,
            supineAHI: applicable(monitoringTime)
                ? (supineEvents * hourInSeconds) / supineTime || 0
                : 0,
            nonSupineAHI: applicable(monitoringTime)
                ? (nonSupineEvents * hourInSeconds) / nonSupineTime || 0
                : 0,
            ODI: applicable(monitoringTime)
                ? (desaturationEvents * hourInSeconds) / monitoringTime
                : 0,
        };

        uniqueCache.set(result, 'annotations');

        return result;
    }
);
export const getQuickViewsSelector = createSelector(
    sortedAnnotationsSelector,
    (state) => state.app.edf.info.headers.recordingLength,
    (annotations, length) => {
        if (annotations.length === 0) {
            return {};
        }

        const result = {
            [TAGS.RESPIRATORY]: {
                label: getFriendlyLabelForTag(TAGS.RESPIRATORY),
                dataset: [],
            },
            [TAGS.DESATURATION]: {
                label: getFriendlyLabelForTag(TAGS.DESATURATION),
                dataset: [],
            },
            [TAGS.POSITION]: {
                label: getFriendlyLabelForTag(TAGS.POSITION),
                dataset: [],
            },
        };

        annotations.reduce((acc, { tag, onset }) => {
            const parentTag = getParentTag(tag);

            if (parentTag === TAGS.EVENT) {
                if (acc[tag]) {
                    acc[tag].dataset.push({ x: onset, y: 0 });
                }
            }

            if (parentTag === TAGS.POSITION) {
                acc[parentTag].dataset.push({ x: onset, y: POSITIONS_ORDER.indexOf(tag) });
            }

            return acc;
        }, result);

        const lastPosition =
            result[TAGS.POSITION].dataset[result[TAGS.POSITION].dataset.length - 1];

        if (lastPosition && Object.keys(lastPosition).length) {
            result[TAGS.POSITION].dataset.push({ x: length, y: lastPosition.y });
        }

        return result;
    }
);

export const isNoAnnotationsSelector = createSelector(
    getQuickViewsSelector,
    (annotations) =>
        Object.values(annotations).reduce((acc, curr) => acc + curr.dataset.length, 0) === 0
);
// endregion

// region aggregations
export const hrSelector = (state) => state.app.edf.info.aggregations.hr;
export const spo2Selector = (state) => state.app.edf.info.aggregations.spo2;
// endregion
